import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  CreateSurveyEntrySessionRequest,
  RefreshSurveyEntrySessionRequest,
  SurveyApiDefinition,
  SurveyApiEnvelope,
  SurveyContext,
  SurveyContextParams,
  SurveyEntrySession,
  SurveySubmissionRequest,
  SurveySubmissionResponse,
} from './survey.models';
import { SATISFACTION_SURVEY_API_CONFIG } from './survey-api.config';

@Injectable({ providedIn: 'root' })
export class SurveyApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SATISFACTION_SURVEY_API_CONFIG);

  getActiveSurvey(params?: SurveyContextParams): Observable<SurveyApiDefinition> {
    return this.http
      .get<SurveyApiEnvelope<SurveyApiDefinition>>(
        `${this.config.apiBaseUrl}/surveys/active`,
        { params: this.toSurveyContextHttpParams(params) }
      )
      .pipe(map((response) => response.data));
  }

  getSurveyContexts(): Observable<SurveyContext[]> {
    return this.http
      .get<SurveyApiEnvelope<SurveyContext[]>>(
        `${this.config.apiBaseUrl}/survey-contexts`
      )
      .pipe(map((response) => response.data));
  }

  createEntrySession(
    request: CreateSurveyEntrySessionRequest
  ): Observable<SurveyEntrySession> {
    return this.http
      .post<SurveyApiEnvelope<SurveyEntrySession>>(
        `${this.config.apiBaseUrl}/survey-entry-sessions`,
        request
      )
      .pipe(map((response) => response.data));
  }

  validateEntryToken(token: string): Observable<SurveyEntrySession> {
    return this.http
      .get<SurveyApiEnvelope<SurveyEntrySession>>(
        `${this.config.apiBaseUrl}/survey-entry-sessions/${encodeURIComponent(token)}`
      )
      .pipe(map((response) => response.data));
  }

  refreshEntrySession(token: string): Observable<SurveyEntrySession> {
    const request: RefreshSurveyEntrySessionRequest = { token };

    return this.http
      .post<SurveyApiEnvelope<SurveyEntrySession>>(
        `${this.config.apiBaseUrl}/survey-entry-sessions/refresh`,
        request
      )
      .pipe(map((response) => response.data));
  }

  submitSurvey(
    request: SurveySubmissionRequest
  ): Observable<SurveySubmissionResponse> {
    return this.http
      .post<SurveyApiEnvelope<SurveySubmissionResponse>>(
        `${this.config.apiBaseUrl}/survey-submissions`,
        request
      )
      .pipe(map((response) => response.data));
  }

  getSubmissionById(id: number): Observable<SurveySubmissionResponse> {
    return this.http
      .get<SurveyApiEnvelope<SurveySubmissionResponse>>(
        `${this.config.apiBaseUrl}/survey-submissions/${id}`
      )
      .pipe(map((response) => response.data));
  }

  private toSurveyContextHttpParams(
    params?: SurveyContextParams
  ): HttpParams | undefined {
    const propertyCode = params?.propertyCode?.trim();
    const surveyTypeCode = params?.surveyTypeCode?.trim();

    if (!propertyCode || !surveyTypeCode) {
      return undefined;
    }

    return new HttpParams()
      .set('propertyCode', propertyCode)
      .set('surveyTypeCode', surveyTypeCode);
  }
}
